import React from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import ExternalLink from 'components/external-link';
import Picture from 'components/picture';
import { isNative } from 'utils/environment';

import {
  AppStoreBadgeContainer,
  AppStores,
  ConfettiLeft,
  ConfettiRight,
  GooglePlayBadgeContainer,
} from './styled';
import { IAppDownloadProps } from './types';

const Panel = styled.div`
  display: none;

  ${Styles.desktop`
    padding: 12rem 0 6rem 0;
    display: block;
  `}
  ${Styles.mobile`
    display: none;
  `}
`;

const Background = styled.div`
  position: relative;
  color: ${Styles.color.white};
  background-color: ${Styles.color.black};
  padding: 0 5%;

  ${Styles.desktop`
    border-radius: 20px;
    padding: 0 62.5px;
  `}
`;

export const Container = styled.div`
  width: 100%;

  ${Styles.desktop`
    width: calc(100% - 6rem);
    max-width: ${Styles.layout.sectionMaxWidth};
    margin: auto;
  `}
`;

export const HeroContent = styled.div`
  display: flex;
  position: relative;
  text-align: center;
  flex-direction: column;
  align-items: center;

  h2 {
    margin: 0;
    line-height: 1.1;
  }

  ${Styles.desktop`
    position: relative;
    flex-direction: row;
  `}
`;

const TextContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  padding: 0 0 62.5px 0;
  margin: 0;
  order: 2;

  h2 {
    text-align: center;
    font-size: 2.4rem;

    ${Styles.desktop`
      font-size: 3rem;
    `}

    small {
      font-size: 3rem;
      left: -5px;
      top: -0.9rem;
    }
  }

  ${Styles.desktop`
    width: 40%;
    min-width: 385px;
    margin-left: auto;
    margin-right: 5%;
    order: 1;
    padding: 112.5px 0 62.5px 0;
  `}

  ${Styles.desktopLarge`
    margin-right: 15%;
  `}
`;

const Description = styled.p`
  color: ${Styles.color.white};
  font-size: 0.9375rem;
  margin: 0.8rem auto;
  max-width: 305px;
  opacity: 0.7;

  ${Styles.desktop`
    max-width: none;
    margin: 0.8rem 0 2.25rem 0;
  `}
`;

const MainImageContainer = styled.div`
  position: relative;
  max-width: 350px;
  width: 90%;
  top: -40px;

  picture > * {
    width: 100%;
    height: auto;
  }

  ${Styles.desktop`
    position: absolute;
    max-width: none;
    width: 40%;
    height: 115%;
    top: -15%;
    right: 103%;
    overflow: hidden;
    transform: translateX(100%);

    picture > * {
      position: absolute;
      left: 0;
      top: 0;
      height: auto;
    }
  `}
`;
const Disclaimer = styled.div`
  margin: 2rem 0 1rem 0;
  color: ${Styles.color.grey.one};
  font-style: normal;
  font-size: 0.75rem;
  font-family: ${Styles.fontFamily.body};
  a {
    color: ${Styles.color.grey.one};
  }
`;

const badgeStyles = css`
  padding: 0.25rem 0.5rem;
  height: 75px;
  width: 175px;
  background: transparent;

  a {
    display: block;
    height: 100%;
    width: 100%;
  }
`;

/* NOTE:
 * Google play app download button inherently has a padding...
 * So... we shrink the apple one to make them closer in size
 * Because by google guidelines it can NOT be smaller than other badges
 */
const AppStoreBadgeContainerShrink = styled(AppStoreBadgeContainer)`
  padding: 3.65%;
`;

const AppDownloadSection: React.FC<IAppDownloadProps> = ({ item: appDownloadSection, loading }) => {
  const { formatMessage } = useIntl();

  if (isNative || loading) {
    return null;
  }

  return (
    <Panel>
      <Container>
        <Background>
          <HeroContent>
            <TextContainer>
              <ConfettiLeft>
                {appDownloadSection?.textDecorationLeft && (
                  <Picture image={appDownloadSection?.textDecorationLeft} alt="" objectFitContain />
                )}
              </ConfettiLeft>
              <ConfettiRight>
                {appDownloadSection?.textDecorationRight && (
                  <Picture
                    image={appDownloadSection?.textDecorationRight}
                    alt=""
                    objectFitContain
                  />
                )}
              </ConfettiRight>
              <h2>{appDownloadSection?.header?.locale}</h2>
              <Description>{appDownloadSection?.copy?.locale}</Description>
              <AppStores>
                <AppStoreBadgeContainerShrink badgeStyles={badgeStyles}>
                  <ExternalLink href="https://apps.apple.com/us/app/tim-hortons/id1143883086">
                    {appDownloadSection?.iOSDownloadImage?.locale && (
                      <Picture
                        image={appDownloadSection?.iOSDownloadImage?.locale}
                        alt=""
                        objectFitContain
                      />
                    )}
                  </ExternalLink>
                </AppStoreBadgeContainerShrink>
                <GooglePlayBadgeContainer badgeStyles={badgeStyles}>
                  <ExternalLink href="https://play.google.com/store/apps/details?id=digital.rbi.timhortons">
                    {appDownloadSection?.androidDownloadImage?.locale && (
                      <Picture
                        image={appDownloadSection?.androidDownloadImage?.locale}
                        alt=""
                        objectFitContain
                      />
                    )}
                  </ExternalLink>
                </GooglePlayBadgeContainer>
              </AppStores>
              <Disclaimer>{formatMessage({ id: 'phoneAppStoresDisclaimers' })}</Disclaimer>
            </TextContainer>
            <MainImageContainer>
              {appDownloadSection?.primaryImage?.locale && (
                <Picture image={appDownloadSection?.primaryImage?.locale} alt="" objectFitContain />
              )}
            </MainImageContainer>
          </HeroContent>
        </Background>
      </Container>
    </Panel>
  );
};

export default AppDownloadSection;
